import React, { useState, type JSX } from 'react';
import { useTheme } from '@shopify/restyle';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Pressable, View } from 'react-native';
import { IconAlertTriangle, IconCircleX, IconDots, IconPlus } from '@tabler/icons-react-native';
import { BottomSheet } from 'app/design/primitives/bottom-sheet';
import { Column, Row } from 'app/design/primitives/box';
import { Button, PressableType } from 'app/design/primitives/button';
import { Modal } from 'app/design/primitives/modal';
import { Text } from 'app/design/primitives/text';
import { useNavigation } from 'app/hooks/navigation';
import { useRejectOneLoan } from 'app/network/loan';
import type { Loan } from 'app/types/loan';
import { canUserCompleteLoan, LoanETA, LoanStatus } from 'app/types/loan';
import type { User } from 'app/types/user';
import { addLoanToCalendar } from 'app/lib/calendar';
import { isDonationListing } from 'app/lib/listing';
import { ModalCalendarSetting } from 'app/screens/profile/my-preferences/calendar/modal';
import type { Theme } from 'app/shopify';
import { ListingSubtype, TransactionMode } from 'app/types/listing';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { isMobileNative } from 'app/utils/web-helpers';
import Toast from 'react-native-toast-message';
import { useMyProfile } from 'app/network/profile';
import { SUPPORT_CHANNEL_URL } from 'app/network/chat/channel';
import { navigateToChat } from 'app/screens/chat/navigator-helper';
import { getLoanInfos, getTranslationSuffix } from '../functions';

function getContextualOptions({
  addToCalendar,
  hasPrimaryAction,
  isLender,
  loanStatus,
  openCancelConfirmation,
  reportIncident,
  t,
  tSuffix,
}: {
  addToCalendar: () => void;
  hasPrimaryAction: boolean;
  isLender: boolean;
  loanStatus?: LoanETA;
  openCancelConfirmation: () => void;
  reportIncident: () => void;
  t: TFunction<'one-loan'>;
  tSuffix: string;
}) {
  const options: { icon: JSX.Element; buttonText: string; onPress: () => void }[] = [];

  if (
    hasPrimaryAction &&
    isMobileNative &&
    loanStatus !== LoanETA.pending &&
    loanStatus !== LoanETA.past &&
    loanStatus !== LoanETA.returned
  ) {
    options.push({
      icon: <IconPlus strokeWidth={ICON_STROKE_WIDTH} />,
      buttonText: t('addToCalendarButtonText'),
      onPress: addToCalendar,
    });
  }

  if (loanStatus !== LoanETA.pending) {
    options.push({
      icon: <IconAlertTriangle strokeWidth={ICON_STROKE_WIDTH} />,
      buttonText: t('contactSupportAboutIncident'),
      onPress: reportIncident,
    });
  }

  if (hasPrimaryAction && (isLender || loanStatus === LoanETA.scheduled || loanStatus === LoanETA.pending)) {
    options.push({
      icon: <IconCircleX strokeWidth={ICON_STROKE_WIDTH} />,
      buttonText: t(`cancelLoanButtonText.${tSuffix}`),
      onPress: openCancelConfirmation,
    });
  }

  return options;
}

export function LoanMenu({ loan, user }: { loan: Loan; user: User }) {
  const theme = useTheme<Theme>();
  const { t } = useTranslation('one-loan');
  const { t: tOneChannel } = useTranslation('one-channel');
  const { data: myProfile } = useMyProfile();
  const navigation = useNavigation();
  const cancelLoan = useRejectOneLoan();
  const [isCancelConfirmationModalOpen, setIsCancelConfirmationModalOpen] = useState(false);
  const [isContextMenuVisible, setIsContextMenuVisible] = useState(false);
  const { loanType, isLender, loanStatus } = getLoanInfos(loan, user);
  const ctaConfirmReturn = canUserCompleteLoan(loan, isLender);
  const tSuffix = getTranslationSuffix({
    isDonation: isDonationListing(loan.listing),
    isSell: loan.listing.transactionMode === TransactionMode.sell,
    isService: loan.listing.subtype === ListingSubtype.service,
    loanType,
  });
  const modalCalendarSettingState = useState(false);
  const addToCalendar = async () => {
    const res = await addLoanToCalendar(tOneChannel, user.id, loan);
    if (res === 'permission-denied') {
      modalCalendarSettingState[1](true);
    } else if (res === 'error') {
      Toast.show({
        type: 'error',
        text1: tOneChannel('genericError', { ns: 'common' }),
        position: 'bottom',
        bottomOffset: 100,
        visibilityTime: 3000,
      });
    }
  };

  const onCancelLoanPress = () => {
    cancelLoan.mutate(loan.id, {
      onSuccess: () => {
        navigation.pop();
      },
    });
  };

  const reportIncident = () => {
    const isInsured =
      user.insured &&
      loan.listing.subtype !== ListingSubtype.service &&
      !isDonationListing(loan.listing) &&
      loan.listing.transactionMode !== TransactionMode.sell;
    navigateToChat(navigation, SUPPORT_CHANNEL_URL, {
      i18nKey: `contactSupportAboutIncidentPrompt.${isInsured ? 'insured' : 'uninsured'}`,
      ns: 'one-loan',
      values: { name: myProfile?.firstName ?? '' },
    });
  };

  // Keep existing calendar/cancellation eligibility; reporting remains available after a loan ends.
  const hasPrimaryAction =
    loan.status !== LoanStatus.Rejected &&
    (ctaConfirmReturn ||
      Boolean(loan.listing.maxParticipants && loan.listing.maxParticipants > 0) ||
      (loan.status !== LoanStatus.Completed && loan.status !== LoanStatus.Returned));

  const contextualOptions = getContextualOptions({
    addToCalendar: () => void addToCalendar(),
    hasPrimaryAction,
    isLender,
    loanStatus,
    openCancelConfirmation: () => setIsCancelConfirmationModalOpen(true),
    reportIncident,
    t,
    tSuffix,
  });

  return (
    <>
      {contextualOptions.length > 0 ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('moreOptions', { ns: 'management' })}
          testID="loan-banner-menu"
          onPress={() => setIsContextMenuVisible(true)}
          style={({ pressed }) => ({
            width: 44,
            height: 44,
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: theme.borderRadii['radius.full'],
            backgroundColor: theme.colors[pressed ? 'surface.tertiary-neutral' : 'surface.secondary-neutral'],
          })}
        >
          <IconDots size={22} strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['text.primary']} />
        </Pressable>
      ) : null}
      <Modal onClose={() => setIsCancelConfirmationModalOpen(false)} open={isCancelConfirmationModalOpen} transparent>
        <Modal.Card>
          <Modal.Title>{t(`cancelLoanModal.title.${tSuffix}`)}</Modal.Title>

          <Column>
            <Text textAlign="center">{t(`cancelLoanModal.text.${tSuffix}`)}</Text>
          </Column>

          <View>
            <Button
              loading={cancelLoan.isPending}
              marginBottom="spacing.general.x4"
              onPress={onCancelLoanPress}
              variant="primary"
            >
              {t(`cancelLoanModal.primaryButtonText.${tSuffix}`)}
            </Button>

            <Button onPress={() => setIsCancelConfirmationModalOpen(false)} variant="secondary">
              {t(`cancelLoanModal.secondaryButtonText.${loanType}`)}
            </Button>
          </View>
        </Modal.Card>
      </Modal>

      <BottomSheet onClose={() => setIsContextMenuVisible(false)} open={isContextMenuVisible}>
        <Column gap="spacing.general.x2" paddingTop="spacing.general.x3" paddingBottom="spacing.general.x4">
          {contextualOptions.map((option) => (
            <Button
              key={option.buttonText}
              variant="plain"
              marginHorizontal="spacing.general.x4"
              accessibilityRole="button"
              accessibilityLabel={option.buttonText}
              style={{
                minHeight: 64,
                paddingHorizontal: 16,
                paddingVertical: 16,
                borderRadius: theme.borderRadii['radius.md'],
                backgroundColor: theme.colors['surface.secondary-neutral'],
              }}
              onPress={() => {
                setIsContextMenuVisible(false);
                // Wait for the existing sheet dismissal before navigation or a second modal.
                setTimeout(option.onPress, 400);
              }}
              pressableType={PressableType.GestureHandler}
            >
              <Row alignItems="center" gap="spacing.general.x4" style={{ width: '100%' }}>
                <View>{option.icon}</View>
                <Text flex={1} variant="bodyBase">
                  {option.buttonText}
                </Text>
              </Row>
            </Button>
          ))}
        </Column>
      </BottomSheet>

      <ModalCalendarSetting modalState={modalCalendarSettingState} />
    </>
  );
}
